import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  EntityCard,
  EntityCardActions,
  EntityCardBody,
  EntityCardFooter,
  EntityCardHeader,
  EntityCardRow,
  Input,
  Select,
  Switch,
  Tabs,
  Textarea,
  cn,
  confirmDialog,
  type Tone,
} from "../../../components/ui";
import Modal from "../../../components/base/Modal";
import {
  exchangeApi,
  apiErrorMessage,
  isCarrierNotVerifiedError,
  isShowcaseResponse,
  showcaseNextPath,
  type Capacity,
  type Compliance,
  type DocTypeSpec,
  type KycDocument,
  type Load,
  type Me,
  type OwnerType,
  type Trip,
  type TripStatus,
  type UnitType,
} from "../exchange/api";
import {
  CITY_OPTIONS,
  PRICING_LABELS,
  TRUST_META,
  TRUST_ORDER,
  UNIT_LABELS,
  UNIT_TYPE_OPTIONS,
  fmtDateTime,
  fmtDay,
  fmtWindow,
  fromLocalInput,
  money,
  nextStep,
  pct,
  relative,
  toDate,
  toLocalInput,
} from "../exchange/format";
import { useAsync, useMe } from "../exchange/useMe";
import { PhoneField, isValidPhone, joinPhone, splitPhone } from "../exchange/PhoneField";
import { isValidRfc, normalizeRfc } from "../../../utils/rfc";
import { fleetDocumentsApi } from "../../../features/fleetDocuments";
import {
  CM_BASE,
  CitySelect,
  ErrorState,
  GateNotice,
  Kpi,
  Loader,
  RouteLine,
  Section,
  TripDetailModal,
  TripList,
  TripStatusBadge,
  TrustBadge,
} from "../exchange/components";

// ===========================================================================
// Cargo Market — espacio del TRANSPORTISTA.
// Todo sale del backend real (/cargoExchange/*): el nivel de confianza se
// calcula con el expediente y la flotilla de Administración de Flotilla, así
// que las unidades, remolques y operadores nunca se capturan dos veces aquí.
// ===========================================================================

const T_BASE = `${CM_BASE}/transportista`;
const ROUTES = {
  panel: `${T_BASE}/panel`,
  verificacion: `${T_BASE}/verificacion`,
  bolsa: `${T_BASE}/bolsa`,
  capacidad: `${T_BASE}/capacidad`,
  viajes: `${T_BASE}/viajes`,
};
const FLEET_ROUTES = { units: "/admin/units", drivers: "/admin/drivers", trailers: "/admin/trailers", trips: "/admin/trips" };

/** Id de carga tal como lo publica el API (el `?load=` viene de la URL). */
const LOAD_ID_RE = /^[A-Za-z0-9_-]{1,80}$/;

/**
 * Con qué llega «Completar verificación» desde la bolsa (CC-CONSOLE#1639): la
 * carga no se pierde, la verificación ofrece volver a ella.
 */
interface ReturnToLoadState {
  returnTo?: string;
  returnFolio?: string;
}

type CarrierUnit = Me["carrier"]["units"][number];
type CarrierTrailer = Me["carrier"]["trailers"][number];
type CarrierDriver = Me["carrier"]["drivers"][number];
type VerifTab = "empresa" | "unidades" | "remolques" | "operadores";

const NEXT_STEP_META: Record<string, { label: string; hint: string; tab: VerifTab; icon: string }> = {
  company_docs: { label: "Completa el expediente de tu empresa", hint: "RFC y CSF, 69-B, acta y poder, INE del representante, permiso SICT y cuenta bancaria.", tab: "empresa", icon: "ri-building-line" },
  unit_docs: { label: "Verifica al menos una unidad", hint: "Tarjeta de circulación, pólizas, físico-mecánica y fotos de una unidad de tu flotilla.", tab: "unidades", icon: "ri-truck-line" },
  driver_docs: { label: "Verifica al menos un operador", hint: "Licencia federal, apto médico, INE + selfie y celular verificado.", tab: "operadores", icon: "ri-steering-2-line" },
  gps: { label: "Integra GPS de WAYAK en una unidad verificada", hint: "Con GPS subes a «Rastreado»: los embarcadores te prefieren y las llegadas se marcan solas por geocerca.", tab: "unidades", icon: "ri-map-pin-user-line" },
  premium: { label: "Llega a Premium", hint: "Sube tu certificación OEA / CTPAT / ISO, cierra más de 20 viajes y mantén 4.5★ o más.", tab: "empresa", icon: "ri-vip-crown-line" },
};

const IN_PROGRESS: TripStatus[] = ["confirmed", "to_pickup", "at_pickup", "loaded", "in_transit", "at_dropoff"];
const closedAtOf = (t: Trip) => t.events.find((e) => e.status === "closed")?.at ?? null;
const isMyTurn = (t: Trip) => !["closed", "cancelled"].includes(t.status) && (t.status === "awarded" || nextStep(t.status)?.actor === "carrier");

function Notice({ tone, icon, children, action }: { tone: "info" | "warning" | "success" | "error"; icon: string; children: ReactNode; action?: ReactNode }) {
  const tones = {
    info: "border-info/30 bg-info/10 text-info",
    warning: "border-warning/30 bg-warning/10 text-warning",
    success: "border-success/30 bg-success/10 text-success",
    error: "border-error/30 bg-error/10 text-error",
  };
  return (
    <div className={cn("rounded-xl border p-3 flex flex-col sm:flex-row sm:items-center gap-3", tones[tone])}>
      <i className={cn(icon, "text-xl shrink-0")} />
      <div className="flex-1 min-w-0 text-sm text-content">{children}</div>
      {action}
    </div>
  );
}

function SandboxHint() {
  const { me } = useMe();
  if (!me?.profile?.sandboxOf) return null;
  return (
    <div className="mb-4">
      <Notice tone="info" icon="ri-flask-line">
        Estás en el <b>Sandbox</b>: las cargas, viajes y documentos de esta empresa son de ejemplo y no afectan la operación real.
      </Notice>
    </div>
  );
}

/** Barra de progreso por los 4 niveles de confianza. */
function TrustProgress({ level }: { level: Me["carrier"]["level"] }) {
  const current = TRUST_ORDER.indexOf(level);
  return (
    <ol className="grid grid-cols-4 gap-2">
      {TRUST_ORDER.map((l, i) => {
        const m = TRUST_META[l];
        const done = i <= current;
        const active = i === current;
        return (
          <li key={l} title={m.hint} className={cn("rounded-lg border px-2 py-2 text-center", active ? "border-primary-500 bg-primary-500/10" : done ? "border-success/30 bg-success/10" : "border-line bg-app")}>
            <i className={cn(m.icon, "text-base", active ? "text-primary-500" : done ? "text-success" : "text-content-faint")} />
            <p className={cn("text-[11px] leading-tight mt-1", done ? "text-content font-medium" : "text-content-muted")}>{m.label}</p>
          </li>
        );
      })}
    </ol>
  );
}

/** Pasos que faltan para el siguiente nivel, como lista accionable. */
function NextStepsChecklist({ steps }: { steps: string[] }) {
  const navigate = useNavigate();
  if (!steps.length) return <p className="text-sm text-success"><i className="ri-checkbox-circle-line mr-1" />Ya estás en el nivel más alto. ¡Excelente!</p>;
  return (
    <ul className="space-y-2">
      {steps.map((s) => {
        const m = NEXT_STEP_META[s] ?? { label: s, hint: "", tab: "empresa" as VerifTab, icon: "ri-checkbox-blank-circle-line" };
        return (
          <li key={s} className="flex items-start gap-3 rounded-lg border border-line bg-app p-3">
            <i className={cn(m.icon, "text-lg text-warning mt-0.5 shrink-0")} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-content">{m.label}</p>
              {m.hint && <p className="text-xs text-content-muted mt-0.5">{m.hint}</p>}
            </div>
            <Button size="sm" variant="outline" className="shrink-0" onClick={() => navigate(`${ROUTES.verificacion}?tab=${m.tab}`, { state: { resolve: s } })}>Resolver</Button>
          </li>
        );
      })}
    </ul>
  );
}

// ===========================================================================
// 1. Panel del transportista
// ===========================================================================

export function TransportistaPanel() {
  const navigate = useNavigate();
  const { me, loading, error, reload } = useMe();
  const board = useAsync(() => exchangeApi.loads("board"), []);
  const trips = useAsync(() => exchangeApi.trips("carrier"), []);
  const [open, setOpen] = useState<Trip | null>(null);

  const stats = useMemo(() => {
    const loads = board.data ?? [];
    const list = trips.data ?? [];
    const now = new Date();
    const thisMonth = list.filter((t) => {
      if (t.status !== "closed") return false;
      const d = toDate(closedAtOf(t));
      return !!d && d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    });
    return {
      published: loads.length,
      matching: loads.filter((l) => l.bestMatch).length,
      backhaul: loads.filter((l) => l.bestMatch?.isBackhaul).length,
      invites: loads.filter((l) => l.invited && !l.myOffer).length,
      toAssign: list.filter((t) => t.status === "awarded").length,
      inProgress: list.filter((t) => IN_PROGRESS.includes(t.status)).length,
      income: thisMonth.reduce((s, t) => s + (t.pricing?.freight || 0), 0),
      incomeTrips: thisMonth.length,
    };
  }, [board.data, trips.data]);

  if (loading) return <Section title="Panel del transportista"><Loader /></Section>;
  if (error || !me) return <Section title="Panel del transportista"><ErrorState error={error || "Sin datos"} onRetry={reload} /></Section>;

  const level = me.carrier.level;
  const nextLevel = TRUST_ORDER[TRUST_ORDER.indexOf(level) + 1];
  const myTurn = (trips.data ?? []).filter(isMyTurn);
  const invites = (board.data ?? []).filter((l) => l.invited && !l.myOffer);

  return (
    <Section
      title="Panel del transportista"
      subtitle="Tu nivel de confianza, las cargas que te convienen y lo que te toca hacer hoy."
      actions={<Button size="sm" icon={<i className="ri-search-line" />} onClick={() => navigate(ROUTES.bolsa)}>Buscar cargas</Button>}
    >
      <SandboxHint />
      <GateNotice role="carrier" />

      <div className="grid gap-4 lg:grid-cols-3 mb-4">
        <Card padding="md" className="lg:col-span-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between mb-4">
            <div>
              <p className="text-xs text-content-muted">Tu nivel de confianza</p>
              <div className="mt-1 flex items-center gap-2">
                <TrustBadge level={level} />
                <span className="text-xs text-content-muted">{TRUST_META[level].hint}</span>
              </div>
            </div>
            <div className="text-xs text-content-muted sm:text-right">
              <p>{me.carrier.stats.closedTrips} viajes cerrados</p>
              <p>{me.carrier.stats.rating ? `${me.carrier.stats.rating.toFixed(1)}★ de calificación` : "Sin calificaciones aún"}</p>
            </div>
          </div>
          <TrustProgress level={level} />
          <div className="mt-4">
            <p className="text-xs font-semibold text-content-muted uppercase tracking-wide mb-2">
              {nextLevel ? `Para llegar a «${TRUST_META[nextLevel].label}»` : "Siguientes pasos"}
            </p>
            <NextStepsChecklist steps={me.carrier.nextSteps} />
          </div>
        </Card>

        <Card padding="md">
          <CardHeader title="Lo que ganas" subtitle="Tú cotizas tu flete; WAYAK cobra su comisión al embarcador encima." />
          <dl className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between gap-2"><dt className="text-content-muted">Tu flete</dt><dd className="font-semibold text-content">100 % para ti</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-content-muted">Comisión WAYAK</dt><dd className="text-content">{pct(me.catalogs.commissionPct)} sobre el flete</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-content-muted">Paga el embarcador</dt><dd className="text-content">flete + comisión</dd></div>
          </dl>
          <p className="mt-3 text-xs text-content-faint">Ejemplo: flete de {money(20000)} → el embarcador paga {money(20000 * (1 + me.catalogs.commissionPct))} y tú recibes {money(20000)}.</p>
        </Card>
      </div>

      {me.carrier.expiring.length > 0 && (
        <div className="mb-4">
          <Notice
            tone="warning"
            icon="ri-time-line"
            action={<Button size="sm" variant="warningSoft" onClick={() => navigate(ROUTES.verificacion)}>Renovar</Button>}
          >
            <p className="font-medium">{me.carrier.expiring.length} documento(s) vencen en los próximos 30 días</p>
            <p className="text-xs text-content-muted mt-0.5">
              {me.carrier.expiring.slice(0, 3).map((e) => `${e.label} (${e.ownerName}) en ${e.daysLeft} d`).join(" · ")}
              {me.carrier.expiring.length > 3 ? " · …" : ""}. Si vencen pierdes el sello y no podrás tomar cargas.
            </p>
          </Notice>
        </div>
      )}

      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 mb-4">
        <Kpi label="Cargas que coinciden con tu capacidad" value={board.loading ? "…" : stats.matching} icon="ri-focus-3-line" hint={`${stats.backhaul} de retorno · ${stats.published} publicadas`} />
        <Kpi label="Invitaciones sin responder" value={board.loading ? "…" : stats.invites} icon="ri-mail-star-line" tone="info" />
        <Kpi label="Viajes por asignar" value={trips.loading ? "…" : stats.toAssign} icon="ri-user-add-line" tone="warning" hint="Asigna unidad y operador" />
        <Kpi label="Viajes en curso" value={trips.loading ? "…" : stats.inProgress} icon="ri-truck-line" tone="info" />
        <Kpi label="Ingresos del mes (flete)" value={trips.loading ? "…" : money(stats.income)} icon="ri-money-dollar-circle-line" tone="success" hint={`${stats.incomeTrips} viaje(s) cerrados`} />
      </div>

      <Card padding="md">
        <CardHeader
          title="Te toca"
          subtitle="Viajes que esperan una acción tuya e invitaciones de embarcadores."
          actions={<Button size="sm" variant="outline" onClick={() => navigate(ROUTES.viajes)}>Ver viajes</Button>}
        />
        <div className="mt-3">
          {trips.loading || board.loading ? (
            <Loader />
          ) : trips.error ? (
            <ErrorState error={trips.error} onRetry={trips.reload} />
          ) : myTurn.length === 0 && invites.length === 0 ? (
            <EmptyState icon="ri-checkbox-circle-line" title="Nada pendiente" description="Cuando ganes una carga o te inviten, aparecerá aquí." />
          ) : (
            <ul className="divide-y divide-line">
              {myTurn.map((t) => {
                const step = nextStep(t.status);
                return (
                  <li key={t.id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-content-faint">{t.folio} · {t.shipperName} · {fmtWindow(t.pickupWindow)}</p>
                      <RouteLine origin={t.origin} destination={t.destination} className="text-sm mt-0.5" />
                    </div>
                    <TripStatusBadge status={t.status} />
                    <Button size="sm" variant={t.status === "awarded" ? "primary" : "outline"} onClick={() => setOpen(t)}>
                      {t.status === "awarded" ? "Asignar unidad" : step ? `Marcar «${step.status === "closed" ? "cerrado" : TRIP_STEP_SHORT[step.status] ?? "siguiente paso"}»` : "Abrir"}
                    </Button>
                  </li>
                );
              })}
              {invites.map((l) => (
                <li key={l.id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-content-faint">{l.folio} · {l.shipperName} te invitó · {fmtWindow(l.pickupWindow)}</p>
                    <RouteLine origin={l.origin} destination={l.destination} className="text-sm mt-0.5" />
                  </div>
                  <Badge tone="info"><i className="ri-mail-star-line mr-1" />Invitación</Badge>
                  <Button size="sm" variant="outline" onClick={() => navigate(`${ROUTES.bolsa}?invitaciones=1`)}>Responder</Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      {open && (
        <TripDetailModal
          trip={open}
          onClose={() => setOpen(null)}
          onChanged={() => {
            void trips.reload();
          }}
        />
      )}
    </Section>
  );
}

const TRIP_STEP_SHORT: Partial<Record<TripStatus, string>> = {
  to_pickup: "en camino",
  at_pickup: "en sitio de carga",
  loaded: "cargado",
  in_transit: "en tránsito",
  at_dropoff: "en sitio de descarga",
  delivered: "entregado",
};

// ===========================================================================
// 2. Verificación (KYC) — empresa, unidades, remolques y operadores
// ===========================================================================

type DocState = "approved" | "expiring" | "expired" | "pending" | "rejected" | "missing";
const DOC_STATE_META: Record<DocState, { label: string; tone: Tone; icon: string }> = {
  approved: { label: "Aprobado", tone: "success", icon: "ri-checkbox-circle-line" },
  expiring: { label: "Por vencer", tone: "warning", icon: "ri-time-line" },
  expired: { label: "Vencido", tone: "error", icon: "ri-calendar-close-line" },
  pending: { label: "En revisión", tone: "info", icon: "ri-hourglass-line" },
  rejected: { label: "Rechazado", tone: "error", icon: "ri-close-circle-line" },
  missing: { label: "Falta", tone: "neutral", icon: "ri-upload-2-line" },
};
const DOC_STATE_ICON_CLASS: Record<DocState, string> = {
  approved: "text-success",
  expiring: "text-warning",
  expired: "text-error",
  pending: "text-info",
  rejected: "text-error",
  missing: "text-content-faint",
};
const DAY_MS = 86_400_000;

function effectiveExpiry(doc: KycDocument, spec: DocTypeSpec): Date | null {
  const own = toDate(doc.expiresAt);
  if (own) return own;
  const issued = toDate(doc.issuedAt);
  return issued && spec.validDays ? new Date(issued.getTime() + spec.validDays * DAY_MS) : null;
}

function docStateOf(doc: KycDocument | undefined, spec: DocTypeSpec): { state: DocState; daysLeft: number | null; expires: Date | null } {
  if (!doc) return { state: "missing", daysLeft: null, expires: null };
  if (doc.status === "pending") return { state: "pending", daysLeft: null, expires: null };
  if (doc.status === "rejected") return { state: "rejected", daysLeft: null, expires: null };
  const expires = effectiveExpiry(doc, spec);
  if (!expires) return { state: "approved", daysLeft: null, expires: null };
  const daysLeft = Math.ceil((expires.getTime() - Date.now()) / DAY_MS);
  return { state: daysLeft <= 0 ? "expired" : daysLeft <= 30 ? "expiring" : "approved", daysLeft, expires };
}

/** Documento más reciente por tipo para un dueño. */
function latestDocs(documents: KycDocument[], ownerType: OwnerType, ownerId: string | null): Record<string, KycDocument> {
  const out: Record<string, KycDocument> = {};
  const ms = (d: KycDocument) => toDate(d.createdAt)?.getTime() ?? 0;
  for (const d of documents) {
    if (d.ownerType !== ownerType) continue;
    if (ownerId && String(d.ownerId) !== String(ownerId)) continue;
    const prev = out[d.docType];
    if (!prev || ms(d) > ms(prev)) out[d.docType] = d;
  }
  return out;
}

interface UploadTarget {
  ownerType: OwnerType;
  ownerId: string | null;
  ownerName: string;
  docType: string;
  spec: DocTypeSpec;
}

const FLEET_OWNER_TYPES: OwnerType[] = ["vehicle", "trailer", "driver"];
const MAX_FILE_BYTES = 10 * 1024 * 1024;

function UploadDocModal({ target, onClose, onSaved }: { target: UploadTarget; onClose: () => void; onSaved: () => void }) {
  const [number, setNumber] = useState("");
  const [issuedAt, setIssuedAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  // Unidades, remolques y operadores suben el archivo (Administración de
  // Flotilla usa el mismo expediente); la empresa sigue con enlace.
  const isFleet = FLEET_OWNER_TYPES.includes(target.ownerType) && !!target.ownerId;
  const urlOk = /^https?:\/\/\S+$/i.test(fileUrl.trim());
  const fileOk = !!file && file.size <= MAX_FILE_BYTES;
  const sourceOk = isFleet ? fileOk || urlOk : urlOk;
  const issuedNeeded = !!target.spec.validDays && !expiresAt;

  const submit = async () => {
    setTouched(true);
    if (!sourceOk || (issuedNeeded && !issuedAt)) return;
    setSaving(true);
    try {
      if (isFleet && file) {
        await fleetDocumentsApi.upload(target.ownerType as "vehicle" | "trailer" | "driver", target.ownerId as string, {
          docType: target.docType,
          number: number.trim() || undefined,
          issuedAt: issuedAt || undefined,
          expiresAt: expiresAt || undefined,
          file,
        });
        toast.success("Enviado a revisión");
        onSaved();
        return;
      }
      await exchangeApi.submitDocument({
        ownerType: target.ownerType,
        ownerId: target.ownerId ?? undefined,
        docType: target.docType,
        number: number.trim() || undefined,
        issuedAt: issuedAt || null,
        expiresAt: expiresAt || null,
        fileUrl: fileUrl.trim(),
      });
      toast.success("Enviado a revisión");
      onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Subir documento · ${target.ownerName}`}
      onClose={onClose}
      width="w-full max-w-xl"
      content={
        <div className="space-y-4">
          <div className="rounded-lg border border-line bg-app p-3">
            <p className="text-sm font-medium text-content">{target.spec.label}</p>
            <p className="text-xs text-content-muted mt-0.5">
              {target.spec.required ? "Obligatorio para el sello «Verificado»." : "Opcional."}
              {target.spec.validDays ? ` Vigencia de ${target.spec.validDays} días desde su emisión si no trae vencimiento.` : ""}
              {target.docType === "licencia_federal" ? " Debe ser licencia federal tipo B, C o E." : ""}
            </p>
          </div>
          <Input label="Número o folio del documento" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="Opcional" />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              type="date"
              label={<>Fecha de emisión{issuedNeeded && <span className="text-error"> *</span>}</>}
              value={issuedAt}
              onChange={(e) => setIssuedAt(e.target.value)}
              error={touched && issuedNeeded && !issuedAt ? "Indica la emisión o el vencimiento" : undefined}
            />
            <Input type="date" label="Fecha de vencimiento" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} hint="Si el documento la trae" />
          </div>
          {isFleet && (
            <div>
              <label className="mb-1 block text-sm font-medium text-content">
                Archivo (PDF o imagen)<span className="text-error"> *</span>
              </label>
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp,image/heic"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-content file:mr-3 file:rounded-full file:border-0 file:bg-primary-500 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-on-primary"
              />
              {touched && file && file.size > MAX_FILE_BYTES ? (
                <p className="mt-1 text-xs text-error">El archivo pesa más de 10 MB</p>
              ) : touched && !sourceOk ? (
                <p className="mt-1 text-xs text-error">Adjunta el archivo o pega un enlace</p>
              ) : (
                <p className="mt-1 text-xs text-content-muted">Máximo 10 MB. Si ya lo tienes en la nube, también puedes pegar el enlace abajo.</p>
              )}
            </div>
          )}
          {!(isFleet && file) && (
            <Input
              label={isFleet ? "O enlace al documento" : <>Enlace al documento (PDF)<span className="text-error"> *</span></>}
              value={fileUrl}
              onChange={(e) => setFileUrl(e.target.value)}
              placeholder="https://…"
              error={touched && !isFleet && !urlOk ? "Pega un enlace válido (https://…)" : undefined}
              hint="Comparte el archivo desde Drive, OneDrive o similar con acceso de lectura."
            />
          )}
          <div className="flex gap-3 border-t border-line pt-4">
            <Button variant="outline" shape="pill" className="flex-1" onClick={onClose}>Cancelar</Button>
            <Button shape="pill" className="flex-1" loading={saving} onClick={submit}>Enviar a revisión</Button>
          </div>
        </div>
      }
    />
  );
}

/** Lista de documentos de un dueño con su estado y botón para subir. */
function DocChecklist({
  ownerType,
  ownerId,
  ownerName,
  compact,
  hazmat,
  onUpload,
}: {
  ownerType: OwnerType;
  ownerId: string | null;
  ownerName: string;
  compact?: boolean;
  hazmat?: boolean;
  onUpload: (t: UploadTarget) => void;
}) {
  const { me } = useMe();
  if (!me) return null;
  const specs = me.catalogs.docTypes[ownerType] ?? {};
  const latest = latestDocs(me.documents, ownerType, ownerType === "company" ? null : ownerId);
  return (
    <ul className={cn(compact ? "space-y-1.5" : "divide-y divide-line")}>
      {Object.entries(specs).map(([docType, spec]) => {
        const doc = latest[docType];
        const { state, daysLeft, expires } = docStateOf(doc, spec);
        const meta = DOC_STATE_META[state];
        const required = spec.required || (!!spec.hazmat && !!hazmat);
        const canUpload = state !== "pending";
        return (
          <li key={docType} className={cn("flex items-start gap-2", compact ? "" : "py-3")}>
            <i className={cn(meta.icon, "mt-0.5 shrink-0", DOC_STATE_ICON_CLASS[state])} />
            <div className="flex-1 min-w-0">
              <p className={cn("text-content", compact ? "text-xs" : "text-sm font-medium")}>{spec.label}</p>
              <div className="mt-1 flex flex-wrap items-center gap-1">
                <Badge size="sm" tone={meta.tone}>{state === "expiring" && daysLeft !== null ? `Vence en ${daysLeft} d` : meta.label}</Badge>
                {required ? <Badge size="sm" tone="warning" variant="outline">Obligatorio</Badge> : <Badge size="sm" tone="neutral" variant="outline">Opcional</Badge>}
                {spec.badge && <Badge size="sm" tone="primary" variant="outline"><i className="ri-award-line mr-0.5" />Insignia</Badge>}
                {spec.hazmat && <Badge size="sm" tone="error" variant="outline">Materiales peligrosos</Badge>}
              </div>
              {state === "rejected" && doc?.reviewNote && <p className="text-xs text-error mt-1">Motivo: {doc.reviewNote}</p>}
              {doc && !compact && (
                <p className="text-xs text-content-faint mt-1">
                  {[doc.number && `Folio ${doc.number}`, expires && `vence ${fmtDay(expires.toISOString())}`, `enviado ${relative(doc.createdAt)}`].filter(Boolean).join(" · ")}
                  {doc.fileUrl && (
                    <>
                      {" · "}
                      <a href={doc.fileUrl} target="_blank" rel="noreferrer" className="text-primary-500 hover:underline">ver archivo</a>
                    </>
                  )}
                </p>
              )}
            </div>
            {canUpload && (
              <Button
                size={compact ? "xs" : "sm"}
                variant={state === "missing" || state === "rejected" || state === "expired" ? "primary" : "outline"}
                className="shrink-0"
                icon={<i className="ri-upload-2-line" />}
                onClick={() => onUpload({ ownerType, ownerId, ownerName, docType, spec })}
              >
                {state === "missing" ? "Subir" : "Actualizar"}
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function complianceBadge(c: Compliance): { label: string; tone: Tone } {
  if (c.ok) return c.expiring.length ? { label: "Verificada · por vencer", tone: "warning" } : { label: "Verificada", tone: "success" };
  if (!c.missing.length && c.pending.length) return { label: "En revisión", tone: "info" };
  return { label: "Incompleta", tone: "warning" };
}

function CompanyProfileForm() {
  const { me, reload } = useMe();
  const p = me?.profile;
  const [legalName, setLegalName] = useState(p?.legalName ?? "");
  const [tradeName, setTradeName] = useState(p?.tradeName ?? "");
  const [rfc, setRfc] = useState(p?.rfc ?? "");
  const [contactName, setContactName] = useState(p?.contactName ?? "");
  const [phone, setPhone] = useState(() => splitPhone(p?.contactPhone));
  const [contactEmail, setContactEmail] = useState(p?.contactEmail ?? "");
  const [carrier, setCarrier] = useState(p?.roles?.carrier ?? true);
  const [shipper, setShipper] = useState(p?.roles?.shipper ?? false);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  // Todos los datos de la ficha son obligatorios.
  const errors = {
    legalName: !legalName.trim() ? "Indica la razón social" : undefined,
    tradeName: !tradeName.trim() ? "Indica el nombre comercial" : undefined,
    rfc: !rfc.trim() ? "Indica el RFC" : !isValidRfc(rfc) ? "RFC inválido (12 caracteres persona moral, 13 persona física)" : undefined,
    contactName: !contactName.trim() ? "Indica la persona de contacto" : undefined,
    contactPhone: !phone.number.trim() ? "Indica el teléfono" : !isValidPhone(phone.code, phone.number) ? (phone.code === "+52" ? "El teléfono debe tener 10 dígitos" : "Teléfono inválido") : undefined,
    contactEmail: !contactEmail.trim() ? "Indica el correo" : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim()) ? "Correo inválido" : undefined,
    roles: !carrier && !shipper ? "Elige al menos un rol" : undefined,
  };
  const shown = (k: keyof typeof errors) => (touched ? errors[k] : undefined);

  const save = async () => {
    setTouched(true);
    if (Object.values(errors).some(Boolean)) return;
    setSaving(true);
    try {
      await exchangeApi.saveProfile({
        legalName: legalName.trim(),
        tradeName: tradeName.trim(),
        rfc: normalizeRfc(rfc),
        contactName: contactName.trim(),
        contactPhone: joinPhone(phone.code, phone.number),
        contactEmail: contactEmail.trim().toLowerCase(),
        roles: { carrier, shipper },
      });
      toast.success(p ? "Ficha actualizada" : "Tu empresa quedó dada de alta en Cargo Market");
      await reload();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card padding="md">
      <CardHeader title="Ficha de la empresa" subtitle={p ? "Así te ven los embarcadores (sin tus datos de contacto hasta confirmar un viaje)." : "Da de alta a tu empresa para empezar tu verificación."} />
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Input required label="Razón social" value={legalName} onChange={(e) => setLegalName(e.target.value)} error={shown("legalName")} />
        <Input required label="Nombre comercial" value={tradeName} onChange={(e) => setTradeName(e.target.value)} error={shown("tradeName")} />
        <Input required label="RFC" value={rfc} maxLength={13} onChange={(e) => setRfc(e.target.value.toUpperCase())} error={shown("rfc")} />
        <Input required label="Persona de contacto" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Nombre y apellido" error={shown("contactName")} />
        <PhoneField required label="Teléfono" code={phone.code} number={phone.number} onChange={setPhone} error={shown("contactPhone")} />
        <Input required label="Correo" type="email" inputMode="email" autoComplete="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="contacto@empresa.com" error={shown("contactEmail")} />
      </div>
      <div className="mt-4">
        <p className="text-sm font-medium text-content mb-2">Cómo operas en Cargo Market<span className="text-error"> *</span></p>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-content">
            <input type="checkbox" checked={carrier} onChange={(e) => setCarrier(e.target.checked)} className="accent-primary-500" />
            Transportista (muevo carga con mi flotilla)
          </label>
          <label className="flex items-center gap-2 text-sm text-content">
            <input type="checkbox" checked={shipper} onChange={(e) => setShipper(e.target.checked)} className="accent-primary-500" />
            Embarcador (publico cargas)
          </label>
        </div>
        {shown("roles") && <p className="text-error text-xs mt-1">{shown("roles")}</p>}
      </div>
      <div className="mt-4 flex justify-end border-t border-line pt-4">
        <Button loading={saving} onClick={save} icon={<i className="ri-save-line" />}>{p ? "Guardar ficha" : "Dar de alta mi empresa"}</Button>
      </div>
    </Card>
  );
}

function FleetLink({ to, label }: { to: string; label: string }) {
  const navigate = useNavigate();
  return (
    <Button size="sm" variant="link" icon={<i className="ri-external-link-line" />} onClick={() => navigate(to)}>
      {label}
    </Button>
  );
}

function UnitCard({ unit, onUpload }: { unit: CarrierUnit; onUpload: (t: UploadTarget) => void }) {
  const { me, reload } = useMe();
  const vehicle = me?.fleet.vehicles.find((v) => v.id === unit.id);
  const initialType: UnitType = me?.carrier.unitTypes[unit.id] ?? vehicle?.unitType ?? "caja_53";
  const initialEco = me?.profile?.assets?.[unit.id]?.economicNumber ?? "";
  const [unitType, setUnitType] = useState<UnitType>(initialType);
  const [eco, setEco] = useState(initialEco);
  const [saving, setSaving] = useState(false);
  const dirty = unitType !== initialType || eco.trim() !== initialEco;
  const badge = complianceBadge(unit);

  const save = async () => {
    setSaving(true);
    try {
      await exchangeApi.saveProfile({ assets: { [unit.id]: { unitType, economicNumber: eco.trim() } } });
      toast.success("Unidad actualizada");
      await reload();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <EntityCard accent={unit.ok ? "success" : "neutral"}>
      <EntityCardHeader
        icon={<i className="ri-truck-line" />}
        iconTone={badge.tone}
        title={unit.name}
        subtitle={[unit.plate, vehicle?.brand, vehicle?.model].filter(Boolean).join(" · ") || "Sin placas"}
        actions={<Badge tone={badge.tone}>{badge.label}</Badge>}
      />
      <EntityCardBody>
        <div>
          {unit.hasGps ? (
            <Badge tone="success"><i className="ri-map-pin-user-line mr-1" />Rastreado por WAYAK</Badge>
          ) : (
            <Badge tone="warning"><i className="ri-map-pin-line mr-1" />Sin GPS — renta un dispositivo WAYAK</Badge>
          )}
        </div>
        <div className="grid gap-2 grid-cols-2">
          <Select size="sm" label="Tipo de unidad" value={unitType} onChange={(e) => setUnitType(e.target.value as UnitType)} options={UNIT_TYPE_OPTIONS} />
          <Input size="sm" label="Número económico" value={eco} onChange={(e) => setEco(e.target.value)} placeholder="Ej. T-104" />
        </div>
        {dirty && (
          <div className="flex justify-end">
            <Button size="xs" loading={saving} onClick={save}>Guardar tipo y número</Button>
          </div>
        )}
        <div className="border-t border-line-soft pt-2">
          <DocChecklist compact ownerType="vehicle" ownerId={unit.id} ownerName={unit.name} onUpload={onUpload} />
        </div>
      </EntityCardBody>
      <EntityCardFooter>
        <FleetLink to={FLEET_ROUTES.units} label="Gestionar en Administración de Flotilla" />
      </EntityCardFooter>
    </EntityCard>
  );
}

function SimpleOwnerCard({
  owner,
  ownerType,
  icon,
  subtitle,
  note,
  fleetRoute,
  onUpload,
}: {
  owner: CarrierTrailer | CarrierDriver;
  ownerType: "trailer" | "driver";
  icon: string;
  subtitle: string;
  note?: string;
  fleetRoute: string;
  onUpload: (t: UploadTarget) => void;
}) {
  const badge = complianceBadge(owner);
  const okLabel = ownerType === "driver" ? badge.label.replace("Verificada", "Verificado").replace("Incompleta", "Incompleto") : badge.label;
  return (
    <EntityCard accent={owner.ok ? "success" : "neutral"}>
      <EntityCardHeader icon={<i className={icon} />} iconTone={badge.tone} title={owner.name} subtitle={subtitle} actions={<Badge tone={badge.tone}>{okLabel}</Badge>} />
      <EntityCardBody>
        {note && <p className="text-content-muted">{note}</p>}
        <DocChecklist compact ownerType={ownerType} ownerId={owner.id} ownerName={owner.name} onUpload={onUpload} />
      </EntityCardBody>
      <EntityCardFooter>
        <FleetLink to={fleetRoute} label="Gestionar en Administración de Flotilla" />
      </EntityCardFooter>
    </EntityCard>
  );
}

function FleetEmpty({ icon, title, route }: { icon: string; title: string; route: string }) {
  const navigate = useNavigate();
  return (
    <EmptyState
      icon={icon}
      title={title}
      description="Tu flotilla se administra en un solo lugar. Dala de alta en Administración de Flotilla y aparecerá aquí para subir sus documentos."
      action={<Button size="sm" icon={<i className="ri-external-link-line" />} onClick={() => navigate(route)}>Ir a Administración de Flotilla</Button>}
    />
  );
}

export function VerificacionTransportistaSection() {
  const { me, loading, error, reload } = useMe();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab") as VerifTab | null;
  // La pestaña vive en la URL: así «Resolver» (que navega a ?tab=…) la cambia
  // aunque ya estés en esta pantalla.
  const tab: VerifTab = tabParam && ["empresa", "unidades", "remolques", "operadores"].includes(tabParam) ? tabParam : "empresa";
  const [upload, setUpload] = useState<UploadTarget | null>(null);
  const tabsRef = useRef<HTMLDivElement>(null);

  const changeTab = (id: string) => {
    const next = new URLSearchParams(params);
    next.set("tab", id);
    setParams(next, { replace: true });
  };

  // «Resolver» trae `state.resolve`: baja hasta la pestaña para que se vea el cambio.
  const resolving = (location.state as { resolve?: string } | null)?.resolve;
  useEffect(() => {
    if (!resolving || loading) return;
    tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [resolving, location.key, loading]);

  // Vino de una carga de la bolsa («Completar verificación»): se le ofrece volver.
  const returnState = (location.state as ReturnToLoadState | null) ?? null;
  const returnTo = returnState?.returnTo && returnState.returnTo.startsWith(`${ROUTES.bolsa}?load=`) ? returnState.returnTo : null;

  if (loading) return <Section title="Verificación"><Loader /></Section>;
  if (error || !me) return <Section title="Verificación"><ErrorState error={error || "Sin datos"} onRetry={reload} /></Section>;

  const c = me.carrier;
  const level = c.level;
  const nextLevel = TRUST_ORDER[TRUST_ORDER.indexOf(level) + 1];
  const okUnits = c.units.filter((u) => u.ok).length;
  const okDrivers = c.drivers.filter((d) => d.ok).length;
  const okTrailers = c.trailers.filter((t) => t.ok).length;
  const companyBadge = complianceBadge(c.company);
  const hasProfile = !!me.profile;

  const tabs = [
    { id: "empresa", label: "Empresa", icon: "ri-building-line" },
    { id: "unidades", label: "Unidades", icon: "ri-truck-line", count: c.units.length, disabled: !hasProfile },
    { id: "remolques", label: "Remolques", icon: "ri-caravan-line", count: c.trailers.length, disabled: !hasProfile },
    { id: "operadores", label: "Operadores", icon: "ri-steering-2-line", count: c.drivers.length, disabled: !hasProfile },
  ];
  const activeTab = hasProfile ? tab : "empresa";

  return (
    <Section title="Verificación" subtitle="Tu camino al sello «Verificado»: empresa + una unidad + un operador con documentos vigentes.">
      <SandboxHint />
      {returnTo && <ReturnToLoadNotice to={returnTo} folio={returnState?.returnFolio} />}

      <Card padding="md" className="mb-4">
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-content-muted">Nivel actual</span>
              <TrustBadge level={level} />
              {nextLevel && <span className="text-sm text-content-muted">→ siguiente: <b className="text-content">{TRUST_META[nextLevel].label}</b></span>}
            </div>
            <TrustProgress level={level} />
            <NextStepsChecklist steps={c.nextSteps} />
          </div>
          <div className="space-y-2 text-sm">
            <SummaryRow icon="ri-building-line" label="Empresa" value={<Badge tone={companyBadge.tone}>{companyBadge.label === "Verificada" ? "Completa" : companyBadge.label}</Badge>} />
            <SummaryRow icon="ri-truck-line" label="Unidades verificadas" value={`${okUnits} de ${c.units.length}`} ok={okUnits > 0} />
            <SummaryRow icon="ri-steering-2-line" label="Operadores verificados" value={`${okDrivers} de ${c.drivers.length}`} ok={okDrivers > 0} />
            <SummaryRow icon="ri-caravan-line" label="Remolques verificados" value={`${okTrailers} de ${c.trailers.length}`} />
            <SummaryRow icon="ri-map-pin-user-line" label="Unidades con GPS WAYAK" value={`${c.units.filter((u) => u.hasGps).length}`} />
            {c.expiring.length > 0 && <p className="text-xs text-warning"><i className="ri-time-line mr-1" />{c.expiring.length} documento(s) vencen en 30 días o menos.</p>}
          </div>
        </div>
      </Card>

      <div ref={tabsRef} className="scroll-mt-4">
        <Tabs items={tabs} activeId={activeTab} onChange={changeTab} className="mb-4" />
      </div>

      {activeTab === "empresa" && (
        <div className="grid gap-4 xl:grid-cols-2">
          <CompanyProfileForm key={me.profile?.legalName ?? "new"} />
          <Card padding="md">
            <CardHeader title="Expediente de la empresa" subtitle="Lo revisa el equipo de WAYAK, normalmente en menos de un día hábil." actions={<Badge tone={companyBadge.tone}>{companyBadge.label === "Verificada" ? "Completo" : companyBadge.label}</Badge>} />
            <div className="mt-2">
              {hasProfile ? (
                <DocChecklist ownerType="company" ownerId={null} ownerName={me.profile?.tradeName || me.profile?.legalName || "Empresa"} onUpload={setUpload} />
              ) : (
                <p className="text-sm text-content-muted py-4">Primero guarda la ficha de tu empresa.</p>
              )}
            </div>
          </Card>
        </div>
      )}

      {activeTab === "unidades" && (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-content-muted">Las unidades vienen de tu flotilla. Aquí sólo indicas su tipo para el mercado y subes su expediente.</p>
            <FleetLink to={FLEET_ROUTES.units} label="Gestionar unidades" />
          </div>
          {c.units.length === 0 ? (
            <FleetEmpty icon="ri-truck-line" title="Aún no tienes unidades en tu flotilla" route={FLEET_ROUTES.units} />
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {c.units.map((u) => <UnitCard key={u.id} unit={u} onUpload={setUpload} />)}
            </div>
          )}
        </>
      )}

      {activeTab === "remolques" && (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-content-muted">Los remolques no son obligatorios para el sello, pero los embarcadores ven si están en regla.</p>
            <FleetLink to={FLEET_ROUTES.trailers} label="Gestionar remolques" />
          </div>
          {c.trailers.length === 0 ? (
            <FleetEmpty icon="ri-caravan-line" title="Aún no tienes remolques en tu flotilla" route={FLEET_ROUTES.trailers} />
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {c.trailers.map((t) => (
                <SimpleOwnerCard key={t.id} owner={t} ownerType="trailer" icon="ri-caravan-line" subtitle={t.plate || "Sin placas"} fleetRoute={FLEET_ROUTES.trailers} onUpload={setUpload} />
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === "operadores" && (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-content-muted">Necesitas al menos un operador verificado. La licencia debe ser federal tipo B, C o E.</p>
            <FleetLink to={FLEET_ROUTES.drivers} label="Gestionar operadores" />
          </div>
          {c.drivers.length === 0 ? (
            <FleetEmpty icon="ri-steering-2-line" title="Aún no tienes operadores en tu flotilla" route={FLEET_ROUTES.drivers} />
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {c.drivers.map((d) => {
                const fd = me.fleet.drivers.find((x) => x.id === d.id);
                return (
                  <SimpleOwnerCard
                    key={d.id}
                    owner={d}
                    ownerType="driver"
                    icon="ri-steering-2-line"
                    subtitle={fd?.licenseId ? `Licencia ${fd.licenseId}${fd.licenseExp ? ` · vence ${fmtDay(fd.licenseExp)}` : ""}` : "Sin licencia registrada en flotilla"}
                    note="Licencia federal tipo B, C o E."
                    fleetRoute={FLEET_ROUTES.drivers}
                    onUpload={setUpload}
                  />
                );
              })}
            </div>
          )}
        </>
      )}

      {upload && (
        <UploadDocModal
          target={upload}
          onClose={() => setUpload(null)}
          onSaved={() => {
            setUpload(null);
            void reload();
          }}
        />
      )}
    </Section>
  );
}

/** «Vienes de la carga X»: botón para volver a la bolsa con esa carga abierta. */
function ReturnToLoadNotice({ to, folio }: { to: string; folio?: string }) {
  const { t } = useTranslation("cargoMarket");
  const navigate = useNavigate();
  return (
    <div className="mb-4" data-testid="verificacion-return-to-load">
      <Notice
        tone="info"
        icon="ri-box-3-line"
        action={
          <Button size="sm" variant="infoSoft" icon={<i className="ri-arrow-go-back-line" />} onClick={() => navigate(to)}>
            {t("verificacion.returnTo.cta")}
          </Button>
        }
      >
        <p className="font-medium">{t("verificacion.returnTo.title", { folio: folio || "" })}</p>
        <p className="text-xs text-content-muted mt-0.5">{t("verificacion.returnTo.body")}</p>
      </Notice>
    </div>
  );
}

function SummaryRow({ icon, label, value, ok }: { icon: string; label: string; value: ReactNode; ok?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-line bg-app px-3 py-2">
      <span className="flex items-center gap-2 text-content-muted">
        <i className={cn(icon, ok === undefined ? "text-content-faint" : ok ? "text-success" : "text-warning")} />
        {label}
      </span>
      <span className="font-medium text-content">{value}</span>
    </div>
  );
}

// ===========================================================================
// 3. Bolsa de cargas
// ===========================================================================

function OfferModal({
  load,
  myCapacity,
  onClose,
  onSaved,
  onShowcase,
  onNotVerified,
}: {
  load: Load;
  myCapacity: Capacity[];
  onClose: () => void;
  onSaved: () => void;
  /** La carga era de demostración: no hay oferta, sólo el aviso del API (y a dónde invita). */
  onShowcase: (message: string, next: string | null) => void;
  /** El API rechazó la oferta con 409 `carrierNotVerified`. */
  onNotVerified: () => void;
}) {
  const { me } = useMe();
  const commissionPct = me?.catalogs.commissionPct ?? 0;
  const rate = useAsync(
    () => exchangeApi.suggestedRate({ origin: load.origin.key, destination: load.destination.key, unitType: load.unitType, backhaul: !!load.bestMatch?.isBackhaul }),
    [load.id],
  );
  const [freight, setFreight] = useState(load.myOffer?.freight ? String(load.myOffer.freight) : "");
  const [note, setNote] = useState(load.myOffer?.note ?? "");
  const capOptions = myCapacity.filter((c) => c.status === "open" && c.unitType === load.unitType);
  const [capacityId, setCapacityId] = useState(load.bestMatch?.capacityId && capOptions.some((c) => c.id === load.bestMatch?.capacityId) ? load.bestMatch.capacityId : "");
  const [saving, setSaving] = useState(false);
  const value = Number(freight);
  const valid = Number.isFinite(value) && value > 0;
  const commission = valid ? Math.round(value * commissionPct * 100) / 100 : 0;

  const submit = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      const result = await exchangeApi.placeOffer({ loadId: load.id, freight: value, note: note.trim() || undefined, capacityId: capacityId || undefined });
      if (isShowcaseResponse(result)) {
        onShowcase(result.message, showcaseNextPath(result.next));
        return;
      }
      toast.success(load.myOffer ? "Oferta actualizada" : "Oferta enviada al embarcador");
      onSaved();
    } catch (err) {
      if (isCarrierNotVerifiedError(err)) onNotVerified();
      else toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`${load.myOffer ? "Actualizar oferta" : "Enviar oferta"} · ${load.folio}`}
      onClose={onClose}
      width="w-full max-w-xl"
      content={
        <div className="space-y-4">
          <div className="rounded-lg border border-line bg-app p-3">
            <RouteLine origin={load.origin} destination={load.destination} className="text-sm" />
            <p className="text-xs text-content-muted mt-1">{fmtWindow(load.pickupWindow)} · {load.merchandise} · {load.weightTons} t · {UNIT_LABELS[load.unitType]}</p>
          </div>

          <div className="rounded-lg border border-info/30 bg-info/10 p-3 text-xs text-content">
            {rate.loading ? (
              "Calculando tarifa sugerida…"
            ) : rate.data ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <p className="flex-1">
                  <i className="ri-lightbulb-line text-info mr-1" />
                  Flete sugerido para esta ruta ({rate.data.km} km{load.bestMatch?.isBackhaul ? ", como retorno" : ""}): <b>{money(rate.data.min)} – {money(rate.data.max)}</b>
                  {rate.data.samples === "history" ? " (según viajes reales)" : " (modelo de costos)"}
                </p>
                <Button size="xs" variant="infoSoft" onClick={() => setFreight(String(rate.data?.suggested ?? ""))}>Usar {money(rate.data.suggested)}</Button>
              </div>
            ) : (
              "No hay tarifa sugerida para esta ruta."
            )}
          </div>

          <Input
            type="number"
            min={0}
            step={100}
            label={<>Tu flete (MXN, sin IVA)<span className="text-error"> *</span></>}
            value={freight}
            onChange={(e) => setFreight(e.target.value)}
            hint="Es lo que recibes tú. La comisión de WAYAK se cobra al embarcador encima."
          />
          <dl className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg border border-line bg-app p-2">
              <dt className="text-[11px] text-content-muted">Recibes</dt>
              <dd className="text-sm font-semibold text-success">{money(valid ? value : null)}</dd>
            </div>
            <div className="rounded-lg border border-line bg-app p-2">
              <dt className="text-[11px] text-content-muted">Comisión WAYAK ({pct(commissionPct)})</dt>
              <dd className="text-sm font-semibold text-content">{money(valid ? commission : null)}</dd>
            </div>
            <div className="rounded-lg border border-line bg-app p-2">
              <dt className="text-[11px] text-content-muted">Paga el cliente</dt>
              <dd className="text-sm font-semibold text-content">{money(valid ? value + commission : null)}</dd>
            </div>
          </dl>

          <Select
            label="Con qué capacidad (opcional)"
            value={capacityId}
            onChange={(e) => setCapacityId(e.target.value)}
            options={[
              { value: "", label: capOptions.length ? "Sin vincular a una publicación" : "No tienes capacidad publicada de este tipo" },
              ...capOptions.map((c) => ({ value: c.id, label: `${c.unitName || c.unitPlate || "Unidad"} · ${c.origin.city} → ${c.destination?.city ?? "cualquier destino"} · ${fmtDay(c.availableAt)}${c.isBackhaul ? " · retorno" : ""}` })),
            ]}
          />
          <Textarea label="Nota para el embarcador (opcional)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej. Unidad con GPS, puedo cargar desde las 6:00" />

          <div className="flex gap-3 border-t border-line pt-4">
            <Button variant="outline" shape="pill" className="flex-1" onClick={onClose}>Cancelar</Button>
            <Button shape="pill" className="flex-1" loading={saving} disabled={!valid} onClick={submit}>{load.myOffer ? "Actualizar oferta" : "Enviar oferta"}</Button>
          </div>
        </div>
      }
    />
  );
}

function LoadCard({
  load,
  canOperate,
  onTake,
  onOffer,
  onWithdraw,
  busy,
}: {
  load: Load;
  canOperate: boolean;
  onTake: () => void;
  onOffer: () => void;
  onWithdraw: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation("cargoMarket");
  const showcase = load.showcase === true;
  const eligible = load.eligible !== false;
  // Sin el sello «Verificado» los botones NO se apagan (el API marca
  // `eligible:false` a quien aún no opera): al pulsarlos la bolsa explica qué
  // falta y lleva a la verificación (CC-CONSOLE#1639). Con el sello, una carga
  // cuyo nivel no se alcanza sí se apaga. Las de demostración siempre se
  // pueden tomar/ofertar: no crean viaje.
  const disabled = canOperate && !eligible && !showcase;
  const offer = load.myOffer;
  const closed = load.offersCloseAt ? (toDate(load.offersCloseAt)?.getTime() ?? Infinity) < Date.now() : false;
  return (
    <EntityCard accent={load.invited ? "info" : load.bestMatch?.isBackhaul ? "success" : "neutral"}>
      <EntityCardHeader
        icon={<i className={load.hazmat ? "ri-alert-line" : "ri-box-3-line"} />}
        iconTone={load.hazmat ? "error" : "primary"}
        title={<RouteLine origin={load.origin} destination={load.destination} />}
        subtitle={`${load.folio} · ${load.shipperName}${load.shipperKind === "forwarder" ? " (agente de carga)" : ""}`}
      />
      <EntityCardBody>
        <div className="flex flex-wrap gap-1">
          {showcase && (
            <span data-badge="showcase" data-load-id={load.id}>
              <Badge size="sm" tone="neutral" variant="outline"><i className="ri-flask-line mr-0.5" />{t("bolsa.showcaseBadge")}</Badge>
            </span>
          )}
          {load.invited && <Badge size="sm" tone="info"><i className="ri-mail-star-line mr-0.5" />Te invitaron</Badge>}
          {load.bestMatch && <Badge size="sm" tone="success"><i className="ri-focus-3-line mr-0.5" />Coincide {load.bestMatch.score}%</Badge>}
          {load.bestMatch?.isBackhaul && <Badge size="sm" tone="success" variant="outline"><i className="ri-arrow-go-back-line mr-0.5" />Retorno vacío</Badge>}
          {load.hazmat && <Badge size="sm" tone="error">Material peligroso</Badge>}
          {load.requirements?.gps && <Badge size="sm" tone="neutral">Requiere GPS</Badge>}
          {load.requirements?.custody && <Badge size="sm" tone="neutral">Custodia</Badge>}
          {load.requirements?.oea && <Badge size="sm" tone="neutral">OEA</Badge>}
          {load.sandboxOf && <Badge size="sm" tone="warning">Ejemplo</Badge>}
        </div>
        <EntityCardRow label="Carga">{fmtWindow(load.pickupWindow)}</EntityCardRow>
        <EntityCardRow label="Entrega">{fmtWindow(load.deliveryWindow)}</EntityCardRow>
        <EntityCardRow label="Mercancía">{load.merchandise}</EntityCardRow>
        <EntityCardRow label="Peso / volumen">{`${load.weightTons} t${load.volumeM3 ? ` · ${load.volumeM3} m³` : ""}`}</EntityCardRow>
        <EntityCardRow label="Unidad">{UNIT_LABELS[load.unitType]}</EntityCardRow>
        <EntityCardRow label="Nivel requerido">
          <span className="inline-flex items-center gap-1">
            <TrustBadge level={load.trustRequired} size="sm" />
            {!eligible && <Badge size="sm" tone="error">No calificas</Badge>}
          </span>
        </EntityCardRow>
        <EntityCardRow label="Modalidad">{PRICING_LABELS[load.pricingMode]}</EntityCardRow>
        {load.pricingMode === "fixed" ? (
          <EntityCardRow label="Tu flete"><span className="text-success font-semibold">{money(load.freightOffered)}</span></EntityCardRow>
        ) : (
          <EntityCardRow label="Ofertas">
            {load.offersCount} recibida(s){load.offersCloseAt ? ` · ${closed ? "cerró" : "cierra"} ${relative(load.offersCloseAt)}` : ""}
          </EntityCardRow>
        )}
        {load.pricingMode !== "fixed" && load.freightOffered ? <EntityCardRow label="Flete de referencia">{money(load.freightOffered)}</EntityCardRow> : null}
        {offer && (
          <div className={cn("rounded-lg border px-3 py-2", offer.status === "accepted" ? "border-success/30 bg-success/10" : "border-info/30 bg-info/10")}>
            <p className="text-content">
              {offer.status === "accepted" ? "Tu oferta fue aceptada" : "Tu oferta"}: <b>{money(offer.freight)}</b> de flete
              <span className="text-content-muted"> · el cliente vería {money(offer.total)}</span>
            </p>
          </div>
        )}
      </EntityCardBody>
      <EntityCardActions>
        {load.pricingMode === "fixed" ? (
          <Button size="sm" className="flex-1" disabled={disabled || busy} loading={busy} icon={<i className="ri-hand-coin-line" />} onClick={onTake}>Tomar carga</Button>
        ) : offer?.status === "active" ? (
          <>
            <Button size="sm" variant="outline" className="flex-1" disabled={disabled || closed} onClick={onOffer}>Mejorar oferta</Button>
            <Button size="sm" variant="dangerSoft" className="flex-1" disabled={busy} loading={busy} onClick={onWithdraw}>Retirar</Button>
          </>
        ) : (
          <Button size="sm" className="flex-1" disabled={disabled || closed || offer?.status === "accepted"} icon={<i className="ri-price-tag-3-line" />} onClick={onOffer}>Enviar oferta</Button>
        )}
      </EntityCardActions>
      <EntityCardFooter>Publicada {relative(load.createdAt) || "—"}</EntityCardFooter>
    </EntityCard>
  );
}

/**
 * Aviso no bloqueante arriba de la bolsa cuando la empresa aún no puede operar
 * (sin ficha, sin rol de transportista o sin el sello «Verificado»): puede
 * ver todas las cargas y ofertar en las de demostración; para las reales,
 * primero la verificación (CC-CONSOLE#1639).
 */
function CarrierVerifyBanner() {
  const { t } = useTranslation("cargoMarket");
  const { me, loading } = useMe();
  const navigate = useNavigate();
  if (loading || !me) return null;
  const hasRole = !!me.profile?.roles?.carrier;
  if (hasRole && me.carrier.canOperate) return null;
  const body = !me.profile ? t("bolsa.verifyBanner.noProfile") : !hasRole ? t("bolsa.verifyBanner.noRole") : t("bolsa.verifyBanner.body");
  return (
    <div className="mb-4" role="status" data-testid="bolsa-verify-banner">
      <Notice
        tone="warning"
        icon="ri-shield-keyhole-line"
        action={
          <Button size="sm" variant="warningSoft" icon={<i className="ri-arrow-right-line" />} onClick={() => navigate(ROUTES.verificacion)}>
            {t("bolsa.verifyBanner.cta")}
          </Button>
        }
      >
        <p className="font-medium">{t("bolsa.verifyBanner.title")}</p>
        <p className="text-xs text-content-muted mt-0.5">{body}</p>
      </Notice>
    </div>
  );
}

/** La carga elegida en la landing (`?load=`): en el tablero o pedida por id. */
interface FocusedLoad {
  id: string;
  load: Load | null;
  missing: boolean;
}

export function BolsaCargasSection() {
  const navigate = useNavigate();
  const { t } = useTranslation("cargoMarket");
  const { me, loading: meLoading, reload: reloadMe } = useMe();
  const [params, setParams] = useSearchParams();
  const board = useAsync(() => exchangeApi.loads("board"), []);
  const myCap = useAsync(() => exchangeApi.capacity("mine"), []);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [unitType, setUnitType] = useState("");
  const [onlyMatch, setOnlyMatch] = useState(false);
  const [onlyInvites, setOnlyInvites] = useState(params.get("invitaciones") === "1");
  const [onlyReal, setOnlyReal] = useState(false);
  const [offerFor, setOfferFor] = useState<Load | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [trip, setTrip] = useState<Trip | null>(null);
  // Flujo del interesado (CC-CONSOLE#1639): la carga con la que llegó, el
  // aviso de una carga de demostración y el de «te falta la verificación».
  const [focus, setFocus] = useState<FocusedLoad | null>(null);
  const [showcaseNotice, setShowcaseNotice] = useState<{ message: string; next: string | null } | null>(null);
  const [notVerifiedFor, setNotVerifiedFor] = useState<Load | null>(null);

  const focusParam = params.get("load");
  const focusId = focusParam && LOAD_ID_RE.test(focusParam) ? focusParam : null;

  // `?load=<id>` abre esa carga al entrar: si está en la primera página del
  // tablero se usa tal cual; si no, se pide por id.
  useEffect(() => {
    if (!focusId || board.loading || focus?.id === focusId) return;
    const found = (board.data ?? []).find((l) => l.id === focusId);
    if (found) {
      setFocus({ id: focusId, load: found, missing: false });
      return;
    }
    let cancelled = false;
    exchangeApi
      .load(focusId)
      .then((r) => {
        if (!cancelled) setFocus({ id: focusId, load: r.load, missing: false });
      })
      .catch(() => {
        if (!cancelled) setFocus({ id: focusId, load: null, missing: true });
      });
    return () => {
      cancelled = true;
    };
  }, [focusId, board.data, board.loading, focus?.id]);

  const clearFocus = () => {
    setFocus(null);
    if (focusParam !== null) {
      const next = new URLSearchParams(params);
      next.delete("load");
      setParams(next, { replace: true });
    }
  };

  const list = useMemo(
    () =>
      (board.data ?? []).filter(
        (l) =>
          (!origin || l.origin.key === origin) &&
          (!destination || l.destination.key === destination) &&
          (!unitType || l.unitType === unitType) &&
          (!onlyMatch || !!l.bestMatch) &&
          (!onlyInvites || !!l.invited) &&
          (!onlyReal || !l.showcase),
      ),
    [board.data, origin, destination, unitType, onlyMatch, onlyInvites, onlyReal],
  );

  const canOperate = !!me?.carrier.canOperate && !!me?.profile?.roles?.carrier;

  /** Cargas reales sólo con el sello; las de demostración, siempre. */
  const requireVerified = (load: Load): boolean => {
    if (load.showcase || canOperate) return true;
    setNotVerifiedFor(load);
    return false;
  };

  const take = async (load: Load) => {
    if (!requireVerified(load)) return;
    const ok = await confirmDialog({
      title: `¿Tomar la carga ${load.folio}?`,
      html: `<p>${load.origin.city} → ${load.destination.city}<br/>Tu flete: <b>${money(load.freightOffered)}</b></p><p style="margin-top:8px;font-size:13px">La carga se asigna al primer transportista verificado que la tome. Después asignas unidad y operador de tu flotilla.</p>`,
      confirmText: "Tomar carga",
    });
    if (!ok) return;
    setBusyId(load.id);
    try {
      const result = await exchangeApi.takeLoad(load.id);
      if (isShowcaseResponse(result)) {
        setShowcaseNotice({ message: result.message, next: showcaseNextPath(result.next) });
        return;
      }
      toast.success(`¡Carga ${load.folio} asignada! Ahora asigna unidad y operador.`);
      setTrip(result);
      clearFocus();
      void board.reload();
    } catch (err) {
      if (isCarrierNotVerifiedError(err)) setNotVerifiedFor(load);
      else toast.error(apiErrorMessage(err));
      void board.reload();
    } finally {
      setBusyId(null);
    }
  };

  const openOffer = (load: Load) => {
    if (!requireVerified(load)) return;
    setOfferFor(load);
  };

  const goToVerification = (load: Load) => {
    setNotVerifiedFor(null);
    navigate(`${ROUTES.verificacion}?tab=empresa`, {
      state: { returnTo: `${ROUTES.bolsa}?load=${encodeURIComponent(load.id)}`, returnFolio: load.folio } satisfies ReturnToLoadState,
    });
  };

  const withdraw = async (load: Load) => {
    if (!load.myOffer) return;
    const ok = await confirmDialog({ title: "¿Retirar tu oferta?", text: `Tu oferta de ${money(load.myOffer.freight)} para ${load.folio} dejará de verse.`, confirmText: "Retirar", tone: "danger", icon: "warning" });
    if (!ok) return;
    setBusyId(load.id);
    try {
      await exchangeApi.withdrawOffer(load.myOffer.id);
      toast.success("Oferta retirada");
      void board.reload();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const renderCard = (l: Load) => (
    <LoadCard
      key={l.id}
      load={l}
      canOperate={canOperate}
      busy={busyId === l.id}
      onTake={() => void take(l)}
      onOffer={() => openOffer(l)}
      onWithdraw={() => void withdraw(l)}
    />
  );

  const filters = (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6 items-end">
      <Select label="Origen" value={origin} onChange={(e) => setOrigin(e.target.value)} options={[{ value: "", label: "Cualquier origen" }, ...CITY_OPTIONS]} />
      <CitySelect label="Destino" value={destination} onChange={setDestination} allowAny />
      <Select label="Tipo de unidad" value={unitType} onChange={(e) => setUnitType(e.target.value)} options={[{ value: "", label: "Todas" }, ...UNIT_TYPE_OPTIONS]} />
      <label className="flex items-center gap-2 text-sm text-content h-10">
        <input type="checkbox" checked={onlyMatch} onChange={(e) => setOnlyMatch(e.target.checked)} className="accent-primary-500" />
        Sólo las que coinciden con mi capacidad
      </label>
      <label className="flex items-center gap-2 text-sm text-content h-10">
        <input type="checkbox" checked={onlyInvites} onChange={(e) => setOnlyInvites(e.target.checked)} className="accent-primary-500" />
        Sólo invitaciones
      </label>
      <label className="flex items-center gap-2 text-sm text-content h-10">
        <input type="checkbox" checked={onlyReal} onChange={(e) => setOnlyReal(e.target.checked)} className="accent-primary-500" data-testid="bolsa-only-real" />
        {t("bolsa.onlyReal")}
      </label>
    </div>
  );

  return (
    <Section
      title="Bolsa de cargas"
      subtitle="Primero tus invitaciones y luego las que mejor encajan con tu capacidad publicada (los retornos vacíos suben)."
      actions={<Button size="sm" variant="outline" icon={<i className="ri-refresh-line" />} onClick={() => void board.reload()}>Actualizar</Button>}
      filters={filters}
    >
      <SandboxHint />
      <CarrierVerifyBanner />
      {focus?.missing && (
        <div className="mb-4" data-testid="bolsa-focus-missing">
          <Notice tone="warning" icon="ri-search-eye-line" action={<Button size="sm" variant="outline" onClick={clearFocus}>{t("bolsa.focus.back")}</Button>}>
            {t("bolsa.focus.notFound")}
          </Notice>
        </div>
      )}
      {!meLoading && me && canOperate && (myCap.data ?? []).every((c) => c.status !== "open") && (
        <div className="mb-4">
          <Notice tone="info" icon="ri-lightbulb-line" action={<Button size="sm" variant="infoSoft" onClick={() => navigate(ROUTES.capacidad)}>Publicar capacidad</Button>}>
            Publica tu capacidad disponible (sobre todo tus regresos vacíos) y te mostraremos qué cargas te convienen, con su % de coincidencia.
          </Notice>
        </div>
      )}
      {board.loading ? (
        <Loader />
      ) : board.error ? (
        <ErrorState error={board.error} onRetry={board.reload} />
      ) : list.length === 0 ? (
        <EmptyState
          icon="ri-search-line"
          title={(board.data ?? []).length ? "Ninguna carga coincide con los filtros" : "No hay cargas abiertas por ahora"}
          description={(board.data ?? []).length ? "Quita algún filtro para ver más." : "Publica tu capacidad y te avisaremos cuando aparezca una carga para ti."}
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{list.map(renderCard)}</div>
      )}

      {focus?.load && (
        <Modal
          title={`${t("bolsa.focus.title")} · ${focus.load.folio}`}
          onClose={clearFocus}
          width="w-full max-w-xl"
          content={
            <div className="space-y-4" data-testid="bolsa-focus">
              <p className="text-sm text-content-muted">{t("bolsa.focus.hint")}</p>
              {/* El aviso de verificación también dentro del foco: la bolsa queda detrás del modal. */}
              <CarrierVerifyBanner />
              {renderCard(focus.load)}
              <div className="flex justify-end border-t border-line pt-4">
                <Button variant="outline" shape="pill" onClick={clearFocus}>{t("bolsa.focus.back")}</Button>
              </div>
            </div>
          }
        />
      )}

      {offerFor && (
        <OfferModal
          load={offerFor}
          myCapacity={myCap.data ?? []}
          onClose={() => setOfferFor(null)}
          onSaved={() => {
            setOfferFor(null);
            clearFocus();
            void board.reload();
          }}
          onShowcase={(message, next) => {
            setOfferFor(null);
            setShowcaseNotice({ message, next });
          }}
          onNotVerified={() => {
            const load = offerFor;
            setOfferFor(null);
            setNotVerifiedFor(load);
          }}
        />
      )}

      {showcaseNotice && (
        <Modal
          title={t("bolsa.showcase.title")}
          onClose={() => setShowcaseNotice(null)}
          width="w-full max-w-lg"
          content={
            <div className="space-y-4" data-testid="bolsa-showcase-notice">
              <div className="flex gap-3 rounded-xl border border-info/30 bg-info/10 p-4">
                <i className="ri-flask-line text-xl text-info shrink-0" />
                <p className="text-sm text-content">{showcaseNotice.message || t("bolsa.showcase.fallback")}</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button shape="pill" className="flex-1" icon={<i className="ri-truck-line" />} onClick={() => navigate(showcaseNotice.next ?? ROUTES.capacidad)}>
                  {t("bolsa.showcase.publishCapacity")}
                </Button>
                <Button
                  shape="pill"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowcaseNotice(null);
                    setOnlyReal(true);
                    clearFocus();
                  }}
                >
                  {t("bolsa.showcase.seeReal")}
                </Button>
              </div>
            </div>
          }
        />
      )}

      {notVerifiedFor && (
        <Modal
          title={t("bolsa.notVerified.title")}
          onClose={() => setNotVerifiedFor(null)}
          width="w-full max-w-lg"
          content={
            <div className="space-y-4" data-testid="bolsa-not-verified">
              <div className="flex gap-3 rounded-xl border border-warning/30 bg-warning/10 p-4">
                <i className="ri-shield-keyhole-line text-xl text-warning shrink-0" />
                <p className="text-sm text-content">{t("bolsa.notVerified.body", { folio: notVerifiedFor.folio })}</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button variant="outline" shape="pill" className="flex-1" onClick={() => setNotVerifiedFor(null)}>
                  {t("bolsa.notVerified.later")}
                </Button>
                <Button shape="pill" className="flex-1" icon={<i className="ri-shield-check-line" />} onClick={() => goToVerification(notVerifiedFor)}>
                  {t("bolsa.notVerified.cta")}
                </Button>
              </div>
            </div>
          }
        />
      )}
      {trip && (
        <TripDetailModal
          trip={trip}
          onClose={() => {
            setTrip(null);
            void reloadMe();
          }}
        />
      )}
    </Section>
  );
}

// ===========================================================================
// 4. Mi capacidad
// ===========================================================================

const CAPACITY_STATUS: Record<Capacity["status"], { label: string; tone: Tone }> = {
  open: { label: "Publicada", tone: "success" },
  booked: { label: "Reservada", tone: "info" },
  cancelled: { label: "Cancelada", tone: "neutral" },
  expired: { label: "Vencida", tone: "neutral" },
};

function defaultAvailable(): string {
  const d = new Date(Date.now() + 2 * 3600 * 1000);
  d.setMinutes(0, 0, 0);
  return toLocalInput(d);
}

function CapacityForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { me } = useMe();
  const [vehicleId, setVehicleId] = useState("");
  const [remolqueId, setRemolqueId] = useState("");
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [availableAt, setAvailableAt] = useState(defaultAvailable());
  const [tons, setTons] = useState("");
  const [m3, setM3] = useState("");
  const [rate, setRate] = useState("");
  const [isBackhaul, setIsBackhaul] = useState(true);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const vehicle = me?.fleet.vehicles.find((v) => v.id === vehicleId) ?? null;
  const unitType: UnitType | null = vehicle ? (me?.carrier.unitTypes[vehicle.id] ?? vehicle.unitType) : null;
  const spec = unitType && me ? me.catalogs.unitTypes[unitType] : null;

  const suggested = useAsync(
    () =>
      origin && destination && unitType
        ? exchangeApi.suggestedRate({ origin, destination, unitType, backhaul: isBackhaul })
        : Promise.resolve(null),
    [origin, destination, unitType, isBackhaul],
  );

  // Al elegir unidad: capacidad por defecto de su tipo y su remolque enganchado.
  useEffect(() => {
    if (!vehicle || !spec) return;
    setTons(String(spec.tons));
    setM3(spec.m3 ? String(spec.m3) : "");
    setRemolqueId(vehicle.remolque_id ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, unitType]);

  if (!me) return <Loader />;
  const unitById = new Map(me.carrier.units.map((u) => [u.id, u]));
  const vehicleOptions = me.fleet.vehicles.map((v) => {
    const u = unitById.get(v.id);
    const ut = me.carrier.unitTypes[v.id] ?? v.unitType;
    const ok = !!u?.ok;
    return {
      value: v.id,
      label: `${v.name || v.plate || v.id}${v.plate ? ` (${v.plate})` : ""} · ${UNIT_LABELS[ut]} · ${v.deviceId ? "con GPS" : "sin GPS"}${ok ? "" : " · expediente incompleto"}`,
      disabled: !ok,
    };
  });

  const submit = async () => {
    setTouched(true);
    if (!vehicleId || !origin || !availableAt || !unitType) return;
    setSaving(true);
    try {
      await exchangeApi.createCapacity({
        vehicleId,
        remolqueId: remolqueId || null,
        origin,
        destination: destination || null,
        availableAt: fromLocalInput(availableAt),
        unitType,
        capacityTons: Number(tons) || undefined,
        capacityM3: Number(m3) || undefined,
        rate: Number(rate) || undefined,
        isBackhaul,
        notes: notes.trim() || undefined,
      });
      toast.success("Capacidad publicada");
      onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {vehicleOptions.every((o) => o.disabled) && (
        <Notice tone="warning" icon="ri-shield-keyhole-line">Ninguna unidad tiene su expediente completo. Termina la verificación de al menos una unidad para publicar capacidad.</Notice>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          label={<>Unidad<span className="text-error"> *</span></>}
          value={vehicleId}
          onChange={(e) => setVehicleId(e.target.value)}
          options={[{ value: "", label: "Elige una unidad de tu flotilla" }, ...vehicleOptions]}
          error={touched && !vehicleId ? "Elige una unidad" : undefined}
          hint={vehicle ? (vehicle.deviceId ? "Rastreada por WAYAK: más confianza para el embarcador." : "Sin GPS: renta un dispositivo WAYAK para subir a «Rastreado».") : undefined}
        />
        <Select
          label="Remolque (opcional)"
          value={remolqueId}
          onChange={(e) => setRemolqueId(e.target.value)}
          options={[{ value: "", label: "Sin remolque" }, ...me.fleet.trailers.map((t) => ({ value: t.id, label: `${t.name || t.id}${t.plate ? ` (${t.plate})` : ""}` }))]}
        />
        <CitySelect label="Disponible en" value={origin} onChange={setOrigin} required error={touched && !origin ? "Elige el origen" : undefined} />
        <CitySelect label="Hacia" value={destination} onChange={setDestination} allowAny />
        <Input type="datetime-local" label={<>Disponible desde<span className="text-error"> *</span></>} value={availableAt} onChange={(e) => setAvailableAt(e.target.value)} hint="Hora del centro de México" />
        <div className="grid grid-cols-2 gap-3">
          <Input type="number" min={0} label="Toneladas" value={tons} onChange={(e) => setTons(e.target.value)} hint={spec ? `Máx. ${spec.tons} t` : undefined} />
          <Input type="number" min={0} label="m³" value={m3} onChange={(e) => setM3(e.target.value)} />
        </div>
      </div>

      <div className="rounded-lg border border-line bg-app p-3 flex items-start gap-3">
        <Switch checked={isBackhaul} onChange={setIsBackhaul} aria-label="Es retorno (viaje vacío)" />
        <div>
          <p className="text-sm font-medium text-content">Es retorno (viaje vacío)</p>
          <p className="text-xs text-content-muted mt-0.5">Las coincidencias priorizan los retornos: tu unidad aparece primero para los embarcadores y tu tarifa sugerida baja para que ganes la carga en vez de regresar vacío.</p>
        </div>
      </div>

      <div className="space-y-2">
        <Input type="number" min={0} step={100} label="Tu flete deseado (MXN, opcional)" value={rate} onChange={(e) => setRate(e.target.value)} hint="Lo que quieres recibir. El embarcador paga además la comisión de WAYAK." />
        {suggested.data ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-info/30 bg-info/10 p-3 text-xs text-content">
            <p className="flex-1">
              <i className="ri-lightbulb-line text-info mr-1" />
              Flete sugerido{isBackhaul ? " de retorno" : ""} ({suggested.data.km} km): <b>{money(suggested.data.min)} – {money(suggested.data.max)}</b>
            </p>
            <Button size="xs" variant="infoSoft" onClick={() => setRate(String(suggested.data?.suggested ?? ""))}>Usar {money(suggested.data.suggested)}</Button>
          </div>
        ) : origin && unitType && !destination ? (
          <p className="text-xs text-content-faint">Elige un destino para ver la tarifa sugerida.</p>
        ) : null}
      </div>

      <Textarea label="Notas (opcional)" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ej. Caja limpia, acepto consolidado" />

      <div className="flex gap-3 border-t border-line pt-4">
        <Button variant="outline" shape="pill" className="flex-1" onClick={onClose}>Cancelar</Button>
        <Button shape="pill" className="flex-1" loading={saving} onClick={submit}>Publicar capacidad</Button>
      </div>
    </div>
  );
}

export function CapacidadSection() {
  const { me } = useMe();
  const navigate = useNavigate();
  const caps = useAsync(() => exchangeApi.capacity("mine"), []);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const canOperate = !!me?.carrier.canOperate && !!me?.profile?.roles?.carrier;

  const cancel = async (c: Capacity) => {
    const ok = await confirmDialog({ title: "¿Retirar esta capacidad?", text: `${c.origin.city} → ${c.destination?.city ?? "cualquier destino"} · ${fmtDateTime(c.availableAt)}`, confirmText: "Retirar", tone: "danger", icon: "warning" });
    if (!ok) return;
    setBusyId(c.id);
    try {
      await exchangeApi.cancelCapacity(c.id);
      toast.success("Capacidad retirada");
      void caps.reload();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const list = [...(caps.data ?? [])].sort((a, b) => Number(b.status === "open") - Number(a.status === "open") || String(a.availableAt).localeCompare(String(b.availableAt)));

  return (
    <Section
      title="Mi capacidad"
      subtitle="Publica dónde y cuándo tendrás unidades libres. Con eso te mostramos las cargas que coinciden."
      actions={<Button size="sm" icon={<i className="ri-add-line" />} disabled={!canOperate} onClick={() => setCreating(true)}>Publicar capacidad</Button>}
    >
      <SandboxHint />
      <GateNotice role="carrier" />
      {caps.loading ? (
        <Loader />
      ) : caps.error ? (
        <ErrorState error={caps.error} onRetry={caps.reload} />
      ) : list.length === 0 ? (
        <EmptyState
          icon="ri-truck-line"
          title="No has publicado capacidad"
          description="Publica tus unidades disponibles, sobre todo los regresos vacíos: son lo que más buscan los embarcadores."
          action={canOperate ? <Button size="sm" icon={<i className="ri-add-line" />} onClick={() => setCreating(true)}>Publicar capacidad</Button> : undefined}
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {list.map((c) => {
            const st = CAPACITY_STATUS[c.status] ?? { label: c.status, tone: "neutral" as Tone };
            return (
              <EntityCard key={c.id} accent={c.status === "open" ? (c.isBackhaul ? "success" : "primary") : "neutral"}>
                <EntityCardHeader
                  icon={<i className="ri-truck-line" />}
                  iconTone={st.tone}
                  title={<RouteLine origin={c.origin} destination={c.destination} />}
                  subtitle={`${c.unitName || "Unidad"}${c.unitPlate ? ` · ${c.unitPlate}` : ""}`}
                  actions={<Badge tone={st.tone}>{st.label}</Badge>}
                />
                <EntityCardBody>
                  <div className="flex flex-wrap gap-1">
                    {c.isBackhaul && <Badge size="sm" tone="success"><i className="ri-arrow-go-back-line mr-0.5" />Retorno vacío</Badge>}
                    {c.hasGps ? <Badge size="sm" tone="success" variant="outline">Con GPS WAYAK</Badge> : <Badge size="sm" tone="neutral" variant="outline">Sin GPS</Badge>}                  </div>
                  <EntityCardRow label="Disponible">{fmtDateTime(c.availableAt)}</EntityCardRow>
                  <EntityCardRow label="Unidad">{UNIT_LABELS[c.unitType]}</EntityCardRow>
                  <EntityCardRow label="Capacidad">{`${c.capacityTons} t${c.capacityM3 ? ` · ${c.capacityM3} m³` : ""}`}</EntityCardRow>
                  <EntityCardRow label="Flete deseado">{c.rate ? money(c.rate) : "A convenir"}</EntityCardRow>
                  {c.notes && <p className="text-content-muted">{c.notes}</p>}
                </EntityCardBody>
                <EntityCardActions>
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => navigate(ROUTES.bolsa)}>Ver cargas</Button>
                  <Button size="sm" variant="dangerSoft" className="flex-1" disabled={c.status !== "open" || busyId === c.id} loading={busyId === c.id} onClick={() => void cancel(c)}>Retirar</Button>
                </EntityCardActions>
              </EntityCard>
            );
          })}
        </div>
      )}

      {creating && (
        <Modal
          title="Publicar capacidad"
          onClose={() => setCreating(false)}
          width="w-full max-w-3xl"
          content={
            <CapacityForm
              onClose={() => setCreating(false)}
              onSaved={() => {
                setCreating(false);
                void caps.reload();
              }}
            />
          }
        />
      )}
    </Section>
  );
}

// ===========================================================================
// 5. Mis viajes
// ===========================================================================

const TRIP_TABS: { id: string; label: string; statuses: TripStatus[] }[] = [
  { id: "por-asignar", label: "Por asignar", statuses: ["awarded"] },
  { id: "en-curso", label: "En curso", statuses: IN_PROGRESS },
  { id: "entregados", label: "Entregados", statuses: ["delivered"] },
  { id: "cerrados", label: "Cerrados", statuses: ["closed"] },
  { id: "cancelados", label: "Cancelados", statuses: ["cancelled"] },
];

export function ViajesTransportistaSection() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const trips = useAsync(() => exchangeApi.trips("carrier"), []);
  const [tab, setTab] = useState<string | null>(null);
  const [open, setOpen] = useState<Trip | null>(null);
  const idParam = params.get("id");

  const counts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const t of TRIP_TABS) out[t.id] = (trips.data ?? []).filter((x) => t.statuses.includes(x.status)).length;
    return out;
  }, [trips.data]);

  // Pestaña por defecto: la primera con pendientes.
  const activeTab = tab ?? (counts["por-asignar"] ? "por-asignar" : counts["en-curso"] ? "en-curso" : "por-asignar");

  useEffect(() => {
    if (!idParam || open?.id === idParam || trips.loading) return;
    const found = (trips.data ?? []).find((t) => t.id === idParam);
    if (found) {
      setOpen(found);
      return;
    }
    let cancelled = false;
    exchangeApi
      .trip(idParam)
      .then((t) => {
        if (!cancelled) setOpen(t);
      })
      .catch((err) => toast.error(apiErrorMessage(err, "No se encontró el viaje")));
    return () => {
      cancelled = true;
    };
  }, [idParam, trips.data, trips.loading, open?.id]);

  const close = () => {
    setOpen(null);
    if (idParam) {
      const next = new URLSearchParams(params);
      next.delete("id");
      setParams(next, { replace: true });
    }
  };

  const current = TRIP_TABS.find((t) => t.id === activeTab) ?? TRIP_TABS[0];
  const list = (trips.data ?? []).filter((t) => current.statuses.includes(t.status));

  return (
    <Section
      title="Mis viajes"
      subtitle="Las cargas que ganaste, del «asignar unidad» al cierre con POD."
      actions={<Button size="sm" variant="outline" icon={<i className="ri-route-line" />} onClick={() => navigate(FLEET_ROUTES.trips)}>Ver en Administración de Flotilla</Button>}
    >
      <SandboxHint />
      <div className="mb-4">
        <Notice tone="info" icon="ri-links-line">
          Al asignar unidad y operador el viaje se crea en <b>Administración de Flotilla › Viajes</b> con el GPS de la unidad, y Centinela lo vigila. No necesitas capturarlo de nuevo.
        </Notice>
      </div>
      <Tabs
        className="mb-4"
        activeId={activeTab}
        onChange={setTab}
        items={TRIP_TABS.map((t) => ({ id: t.id, label: t.label, count: counts[t.id], countLoading: trips.loading }))}
      />
      {trips.loading ? (
        <Loader />
      ) : trips.error ? (
        <ErrorState error={trips.error} onRetry={trips.reload} />
      ) : (
        <TripList trips={list} onOpen={setOpen} />
      )}
      {open && (
        <TripDetailModal
          trip={open}
          onClose={close}
          onChanged={() => {
            void trips.reload();
          }}
        />
      )}
    </Section>
  );
}
